import type { CommunityList, LabelInValue } from 'models';
import { type ComponentProps, useMemo, useState } from 'react';
import { InfinityScrollSelect } from 'ui';
import { useDebounce } from 'common/hooks';
// TODO: check if this is the correct way to import the query - 82948
import { useCommunitiesInfiniteQuery } from '../../queries/useCommunitiesInfiniteQuery';

export type CommunitySelectProperties<T extends LabelInValue> = {
  createOption: (value: CommunityList) => T;
  query?: Partial<Parameters<typeof useCommunitiesInfiniteQuery>[0]>;
} & Omit<ComponentProps<typeof InfinityScrollSelect<T>>, 'handleRequestMoreData' | 'hasMoreData'>;

const CommunitySelect = <T extends LabelInValue>({ createOption, query, ...props }: CommunitySelectProperties<T>) => {
  const [search, setSearch] = useState<string>();
  const { isDebouncing, value } = useDebounce(search);

  const {
    data: communities,
    fetchNextPage: fetchNextPageOfCommunities,
    hasNextPage: hasMoreCommunities,
    isFetching: isFetchingCommunities,
  } = useCommunitiesInfiniteQuery({
    filter: { search: value, ...query?.filter },
    pagination: { pageSize: 10, ...query?.pagination },
  });

  const options = useMemo(
    () => communities?.pages.flatMap((page) => page.data.map((community) => createOption(community))),
    [createOption, communities],
  );

  return (
    <InfinityScrollSelect<T>
      fieldNames={{ label: 'optionLabel' }}
      fullWidth
      handleRequestMoreData={fetchNextPageOfCommunities}
      hasMoreData={hasMoreCommunities}
      loading={isFetchingCommunities || isDebouncing}
      onSearch={setSearch}
      optionLabelProp="optionLabel"
      options={options}
      {...props}
    />
  );
};

export default CommunitySelect;
